import React, { useEffect, useRef } from "react";
import { FormikProps, getIn } from "formik";
import { usePlacesWidget } from "react-google-autocomplete";
import { addressInitialValues, FormikAddress } from "utils/addressFormat";

interface UseGooglePlacesFieldOptions {
  formik: FormikProps<any>;
  parent?: string;
  customAddressName?: boolean;
  enforce?: boolean;
}

const US_TERRITORIES = ["PR"];

function parseGooglePlace(
  place: any,
  customAddressName: boolean,
): FormikAddress | null {
  if (!place?.address_components) return null;

  const components: any = {};
  place.address_components.forEach((component: any) => {
    component.types.forEach((type: string) => {
      components[type] = component;
    });
  });

  const streetNumber = components.street_number?.long_name ?? "";
  const route = components.route?.long_name ?? "";
  const fullAddress = [streetNumber, route].filter(Boolean).join(" ");

  const stateCode = components.administrative_area_level_1?.short_name ?? "";
  const countryCode = components.country?.short_name ?? "";

  // Territories should be treated as US with territory as state
  const isTerritory = US_TERRITORIES.includes(countryCode);

  return {
    ...(!customAddressName && {
      addressName: place.formatted_address || "",
    }),
    address1: fullAddress,
    address2: "",
    city: components.locality?.long_name ?? "",
    state: isTerritory ? countryCode : stateCode,
    zipcode: components.postal_code?.long_name ?? "",
    country: isTerritory ? "US" : countryCode,
    defaultLocation: false,
    geocode: place.geometry?.location
      ? {
          x: place.geometry.location.lng(),
          y: place.geometry.location.lat(),
        }
      : undefined,
  };
}

/**
 * Wires a Google Places autocomplete input to a Formik form.
 *
 * - Returns `addressRef` for the input that should host the autocomplete.
 * - Returns `onChange` / `onBlur` to wire to that input.
 * - When `enforce` is true, blurring without picking a suggestion clears the
 *   typed `addressName` (so users can't free-type a fake address).
 *
 * The blur logic uses a setTimeout to defer reading the selection state —
 * Google's `onPlaceSelected` fires *after* the input's blur event, so reading
 * synchronously would see stale state unfortunately.
 */
export function usePlacesAutoComplete({
  formik,
  parent,
  customAddressName = false,
  enforce = false,
}: UseGooglePlacesFieldOptions) {
  const selectedAddressRef = useRef<any>(null);

  const setAddressFields = (address: Record<string, any>) => {
    Object.entries(address).forEach(([key, value]) => {
      const path = parent ? `${parent}.${key}` : key;
      formik.setFieldValue(path, value);
    });
  };

  const { ref: addressRef } = usePlacesWidget<HTMLInputElement>({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    onPlaceSelected: (place) => {
      const parsed = parseGooglePlace(place, customAddressName);
      if (!parsed) {
        setAddressFields(addressInitialValues);
        selectedAddressRef.current = null;
        return;
      }
      selectedAddressRef.current = parsed;
      setAddressFields(parsed);
    },
    options: {
      types: ["geocode", "establishment"],
      componentRestrictions: { country: ["us", "pr"] },
    },
  });

  // Hydrate the ref from existing formik values (e.g., editing a saved record),
  // otherwise blurring without re-selecting would clear an already-valid address.
  useEffect(() => {
    const addressObj = parent ? getIn(formik.values, parent) : formik.values;
    if (
      !selectedAddressRef.current &&
      addressObj &&
      typeof addressObj === "object" &&
      addressObj.address1 &&
      addressObj.address1.trim() !== ""
    ) {
      selectedAddressRef.current = addressObj;
    }
  }, [formik.values, parent]);

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(event);
    if (!enforce) return;

    // Defer so Google Places onPlaceSelected can populate selectedAddressRef
    // first when the user clicks a suggestion
    setTimeout(() => {
      const current = selectedAddressRef.current;
      if (current) {
        setAddressFields(current);
      } else {
        const path = parent ? `${parent}.addressName` : "addressName";
        formik.setFieldValue(path, "");
      }
    }, 200);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    if (enforce && event.target.value === "") {
      selectedAddressRef.current = null;
    }
  };

  return { addressRef, onBlur, onChange };
}

export default usePlacesAutoComplete;
